import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline as CheckIcon } from "@mui/icons-material";
import { green } from "@mui/material/colors";
import axios from "axios";

import { nuclearn_api_url } from "src/App";
import {
  ChatMessageWithParts,
  ContentType,
  UserProfileFormPromptData,
} from "src/views/assistant/types";
import { useChatStreamStore } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useChatContext } from "../../context/ChatContext";
import { useOnboardingStore } from "src/views/assistant/stores/OnboardingStore";
import { NucToast } from "src/reusable/components/Toast";

interface UserProfileFormPromptProps {
  /** Raw text containing the JSON prompt payload */
  text: string;
  /** Whether the chat is still streaming (disables the form while agent runs) */
  isChatConnected: boolean;
  /** Message id this prompt belongs to — used to persist submission into history */
  messageId?: string;
  /** Index of this content part within the message */
  partIndex?: number;
}

const slugFieldKey = (key: string) =>
  key
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * UserProfileFormPrompt
 *
 * Inline form rendered during FTUX module 1. All fields live on
 * OnboardingStore so the Verification pane can mirror / edit the same
 * values. Submitting posts the values as a JSON payload on /chat/send;
 * OnboardingSession parses it out of the input queue on the backend.
 */
const UserProfileFormPrompt = memo(
  ({
    text,
    isChatConnected,
    messageId,
    partIndex,
  }: UserProfileFormPromptProps) => {
    const { dialogueId } = useChatContext();
    // Host chat's store — useAtomAssistChat here used to clobber the host
    // surface's stream config with this card's minimal one.
    const streamStore = useChatStreamStore();

    const {
      profileFormFields,
      profileFormValues,
      profileFormSubmitted,
      setProfileFormFields,
      setProfileFormValue,
      setProfileFormSubmitted,
    } = useOnboardingStore();

    const { promptData, historicalSubmission } = useMemo<{
      promptData: UserProfileFormPromptData | null;
      historicalSubmission: {
        values: Record<string, string>;
        submitted_at: string;
      } | null;
    }>(() => {
      try {
        let jsonText = text;
        if (text.includes("<tool_response>")) {
          jsonText = text
            .replace(/^<tool_response>\s*/, "")
            .replace(/\s*<\/tool_response>$/, "");
        }
        const parsed = JSON.parse(jsonText);
        if (parsed?.type !== "user_profile_form") {
          return { promptData: null, historicalSubmission: null };
        }
        return {
          promptData: parsed as UserProfileFormPromptData,
          historicalSubmission: parsed.user_form_submission ?? null,
        };
      } catch {
        return { promptData: null, historicalSubmission: null };
      }
    }, [text]);

    // Hydrate OnboardingStore with the fields the first time we see this
    // prompt so the Verification pane can render the same form. If the
    // message already carries a prior submission (e.g. rehydrating history on
    // page reload), treat it as submitted.
    useEffect(() => {
      if (!promptData) return;
      if (
        !profileFormFields ||
        profileFormFields.length !== promptData.fields.length
      ) {
        setProfileFormFields(promptData.fields);
      }
      if (historicalSubmission && !profileFormSubmitted) {
        setProfileFormSubmitted(true);
      }
      // profileFormSubmitted & profileFormFields checked in guards above — not
      // adding to the dep list would cause an infinite set loop.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promptData, historicalSubmission]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = useCallback(async () => {
      if (!promptData || profileFormSubmitted || isSubmitting) return;
      setIsSubmitting(true);

      const values: Record<string, string> = {};
      for (const f of promptData.fields) {
        values[f.field] = (profileFormValues[f.field] ?? "").trim();
      }
      // Compute once so the payload POSTed to the backend and the
      // payload persisted into the dialogue's tool_response text share
      // exactly the same submitted_at. A second Date.now() would drift
      // by milliseconds and complicate any downstream correlation.
      const submittedAt = new Date().toISOString();
      const payload = JSON.stringify({
        user_form_submission: { values, submitted_at: submittedAt },
      });

      try {
        // Send first, persist-into-dialogue only on success. Doing the
        // optimistic setDialogue up front would flip isLocked=true via
        // historicalSubmission, so a POST failure would leave the user
        // staring at "Profile saved ✓" with no way to retry despite the
        // backend never having received the submission.
        await axios.post(`${nuclearn_api_url}/chat/send/${dialogueId}`, {
          content: payload,
        });
        if (streamStore === null) {
          // The submission landed; only the in-session part patch needs the
          // host store. A reload shows the durable annotation.
          console.warn("[UserProfileFormPrompt:no-host-store]", { dialogueId });
        }
        if (
          messageId !== undefined &&
          partIndex !== undefined &&
          streamStore !== null
        ) {
          streamStore.getState().setDialogue((prev: ChatMessageWithParts[]) =>
            prev.map((msg) => {
              if (msg.id !== messageId) return msg;
              const updatedParts = msg.contentParts.map((part, idx) => {
                if (idx !== partIndex) return part;
                if (part.type !== ContentType.TOOL_RESPONSE) return part;
                try {
                  let jsonText = part.text;
                  const hasOpenTag = jsonText.includes("<tool_response>");
                  const hasCloseTag = jsonText.includes("</tool_response>");
                  if (hasOpenTag)
                    jsonText = jsonText.replace(/^<tool_response>\s*/, "");
                  if (hasCloseTag)
                    jsonText = jsonText.replace(/\s*<\/tool_response>$/, "");
                  const parsed = JSON.parse(jsonText);
                  const updated = {
                    ...parsed,
                    user_form_submission: {
                      values,
                      submitted_at: submittedAt,
                    },
                  };
                  let newText = JSON.stringify(updated);
                  if (hasOpenTag) newText = "<tool_response>" + newText;
                  if (hasCloseTag) newText = newText + "</tool_response>";
                  return { ...part, text: newText };
                } catch {
                  return part;
                }
              });
              return { ...msg, contentParts: updatedParts };
            }),
          );
        }
        setProfileFormSubmitted(true);
      } catch {
        // Silent failure would leave the user thinking the save went
        // through — raise a visible error so they know to retry. The
        // form stays unlocked (profileFormSubmitted is still false and
        // isSubmitting flips back in finally) so retry is immediate.
        NucToast({
          message:
            "Couldn't save your profile — check your connection and try again.",
          type: "error",
          hideProgressBar: false,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [
      promptData,
      profileFormSubmitted,
      isSubmitting,
      profileFormValues,
      messageId,
      partIndex,
      streamStore,
      dialogueId,
      setProfileFormSubmitted,
    ]);

    if (!promptData) return null;

    const isLocked = profileFormSubmitted || Boolean(historicalSubmission);

    // Once submitted, collapse the card to a single-line confirmation so the
    // next assistant message isn't buried below five disabled text fields.
    // The Verification pane carries the full editable form for post-save
    // tweaks.
    if (isLocked) {
      return (
        <Box
          data-testid="aa-ftux-profile-saved-chip"
          sx={{
            mt: 1,
            px: 1.5,
            py: 1,
            border: "1px solid",
            borderColor: green[300],
            borderRadius: 2,
            bgcolor: "background.paper",
            display: "inline-flex",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckIcon sx={{ color: green[600] }} fontSize="small" />
            <Typography variant="caption" color="text.secondary">
              Profile saved — you can edit it anytime in the Verification pane.
            </Typography>
          </Stack>
        </Box>
      );
    }

    return (
      <Box
        data-testid="aa-ftux-profile-form"
        sx={{
          mt: 1,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={2}>
          {promptData.fields.map((f) => {
            const slug = slugFieldKey(f.field);
            return (
              <TextField
                key={f.field}
                data-testid={`aa-ftux-profile-field-${slug}`}
                inputProps={{
                  "data-testid": `aa-ftux-profile-field-${slug}-inner`,
                }}
                label={f.label}
                placeholder={f.prompt}
                fullWidth
                size="small"
                disabled={!isChatConnected}
                value={profileFormValues[f.field] ?? ""}
                onChange={(e) => setProfileFormValue(f.field, e.target.value)}
              />
            );
          })}

          <Button
            data-testid="aa-ftux-profile-submit"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isChatConnected || isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{ alignSelf: "flex-start" }}
          >
            {promptData.submit_label}
          </Button>
        </Stack>
      </Box>
    );
  },
);

UserProfileFormPrompt.displayName = "UserProfileFormPrompt";

export default UserProfileFormPrompt;
